import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-[#FFFDF5] overflow-hidden pt-20 pb-16"
    >
      {/* Background accent shapes */}
      <div className="absolute top-20 right-0 w-64 h-64 md:w-96 md:h-96 bg-[#FFD166] rounded-full opacity-20 blur-3xl" />
      <div className="absolute bottom-20 left-0 w-48 h-48 md:w-72 md:h-72 bg-[#FE4A90] rounded-full opacity-15 blur-3xl" />

      <div className="px-6 md:px-12 lg:px-24 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <p
                data-testid="hero-label"
                className="font-mono text-sm sm:text-base uppercase tracking-widest font-bold text-[#FE4A90] mb-4"
              >
                Web Designer & Creative
              </p>
            </motion.div>

            <motion.h1
              data-testid="hero-title"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.34, 1.56, 0.64, 1] }}
              className="font-heading text-5xl sm:text-6xl lg:text-8xl tracking-tighter text-[#0A0A0A] uppercase leading-[0.9]"
            >
              Connor
              <br />
              <span className="text-stroke">Corcoran</span>
            </motion.h1>

            <motion.p
              data-testid="hero-description"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
              className="font-body text-base sm:text-lg text-[#525252] mt-8 max-w-lg leading-relaxed"
            >
              I build high-quality websites that help local businesses stand out,
              get found, and grow. RISD-trained designer based in Oakland, building sites that bring in business.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href="#work"
                data-testid="hero-cta-work"
                className="brutal-btn bg-[#0A0A0A] text-[#FFFDF5] px-8 py-4 text-sm inline-block"
              >
                See My Work
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="brutal-btn bg-[#FFD166] text-[#0A0A0A] px-8 py-4 text-sm inline-block"
              >
                Get in Touch
              </a>
            </motion.div>
          </div>

          {/* Right: 3D Image Card */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div
              data-testid="hero-image-card"
              className="brutal-card bg-[#06D6A0] p-3 md:p-4 rotate-2 hover:rotate-0 transition-transform duration-300"
            >
              <picture>
                <source srcSet="/hero-3d.webp" type="image/webp" />
                <img
                  src="/hero-3d.jpg"
                  alt="Whimsical 3D render of a floating island with a vintage computer displaying CORCORAN, surrounded by creative objects"
                  className="w-full aspect-square object-cover border-2 border-[#0A0A0A]"
                  width={3160}
                  height={3160}
                  loading="eager"
                  fetchPriority="high"
                />
              </picture>
              <p className="font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] mt-3 text-center">
                Design + Code + 3D
              </p>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
        >
          <span className="font-mono text-xs uppercase tracking-widest text-[#525252]">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowDown size={20} className="text-[#0A0A0A]" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
